/**
 * Mobile Bottom Navigation Component (Day-15 Mobile UX)
 * 
 * Requirements:
 * - Bottom navigation bar
 * - Large touch targets (min 48px)
 * - Gesture-friendly layouts
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Home, Bell, FileText, Calendar, User, Plus } from 'lucide-react';

// Touch target minimum size
const MIN_TOUCH_SIZE = 48;

// Navigation items
const NAV_ITEMS = [
  { path: '/mobile', icon: Home, label: 'Home', exact: true },
  { path: '/mobile/notifications', icon: Bell, label: 'Alerts' },
  { path: '/mobile/sync', icon: FileText, label: 'Sync' },
];

/**
 * Mobile Bottom Navigation Bar
 */
export function MobileBottomNav({ className }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <nav 
      className={cn(
        'fixed bottom-0 left-0 right-0 bg-background border-t z-50 safe-area-inset-bottom',
        className
      )}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around px-2 py-1">
        {NAV_ITEMS.map(({ path, icon: Icon, label, exact }) => {
          const active = isActive(path, exact);
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={cn(
                'flex flex-col items-center justify-center py-2 px-4 rounded-lg transition-colors',
                'min-w-[48px] min-h-[48px]', // Minimum touch size
                active 
                  ? 'text-primary bg-primary/10' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
              style={{ minWidth: MIN_TOUCH_SIZE, minHeight: MIN_TOUCH_SIZE }}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs mt-0.5 font-medium">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Floating Action Button for mobile
 */
export function MobileFAB({ 
  icon: Icon = Plus, 
  onClick, 
  label,
  className,
  variant = 'primary',
}) {
  const variants = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'fixed bottom-20 right-4 z-40 rounded-full p-4 transition-all',
        'min-w-[56px] min-h-[56px] flex items-center justify-center',
        'active:scale-95',
        variants[variant],
        className
      )}
      style={{ minWidth: 56, minHeight: 56 }} // Larger than min touch size for FAB
      data-testid="mobile-fab"
      aria-label={label}
    >
      <Icon className="h-6 w-6" />
    </button>
  );
}

/**
 * Sticky Action Button Bar for forms
 */
export function MobileStickyActions({ children, className }) {
  return (
    <div 
      className={cn(
        'fixed bottom-0 left-0 right-0 bg-background border-t p-4 z-40',
        'safe-area-inset-bottom',
        className
      )}
      data-testid="mobile-sticky-actions"
    >
      <div className="flex gap-3">
        {children}
      </div>
    </div>
  );
}

/**
 * Touch-friendly button with minimum size
 */
export function TouchButton({ 
  children, 
  className, 
  variant = 'default',
  size = 'default',
  ...props 
}) {
  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  };

  const sizes = {
    default: 'min-h-[48px] px-4 py-3 text-base',
    lg: 'min-h-[56px] px-6 py-4 text-lg',
    icon: 'min-h-[48px] min-w-[48px] p-3',
  };

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        'active:scale-[0.98]',
        variants[variant],
        sizes[size],
        className
      )}
      style={{ minHeight: MIN_TOUCH_SIZE }}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Touch-friendly list item
 */
export function TouchListItem({ 
  children, 
  onClick, 
  className,
  active = false,
  ...props 
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-left',
        'min-h-[48px] transition-colors rounded-lg',
        'hover:bg-muted/50 active:bg-muted',
        active && 'bg-primary/5 text-primary',
        className
      )}
      style={{ minHeight: MIN_TOUCH_SIZE }}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Mobile page container with proper padding for bottom nav
 */
export function MobilePageContainer({ children, className, hasBottomNav = true, hasStickyActions = false }) {
  return (
    <div 
      className={cn(
        'min-h-screen bg-background',
        hasBottomNav && 'pb-20', // Space for bottom nav
        hasStickyActions && 'pb-24', // Space for sticky actions
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Mobile header with back button
 */
export function MobileHeader({ 
  title, 
  subtitle,
  onBack,
  rightAction,
  className,
}) {
  const navigate = useNavigate();

  return (
    <div className={cn('sticky top-0 z-10 bg-primary text-primary-foreground', className)}>
      <div className="flex items-center gap-3 px-4 py-3">
        {onBack !== false && (
          <TouchButton
            variant="ghost"
            size="icon"
            onClick={onBack || (() => navigate(-1))}
            className="text-primary-foreground hover:bg-primary-foreground/10 -ml-2"
            data-testid="mobile-back-btn"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </TouchButton>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold truncate">{title}</h1>
          {subtitle && <p className="text-xs opacity-80 truncate">{subtitle}</p>}
        </div>
        {rightAction}
      </div>
    </div>
  );
}

/**
 * Gesture-friendly swipeable container
 */
export function SwipeableContainer({ 
  children, 
  onSwipeLeft, 
  onSwipeRight,
  threshold = 50,
  className,
}) {
  const touchStartX = React.useRef(0);
  const touchEndX = React.useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0 && onSwipeLeft) {
        onSwipeLeft();
      } else if (diff < 0 && onSwipeRight) {
        onSwipeRight();
      }
    }
  };

  return (
    <div
      className={className}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {children}
    </div>
  );
}

export default MobileBottomNav;
